<template>
  <div :class="cn('flex flex-col', fill && 'min-h-42 flex-1')" aria-hidden="true" data-slot="chart-skeleton">
    <!-- The chart it stands in for, drawn as stubs: the scale down the side, the
         grid, a ghost of the marks, the dates and the legend, at the chart's own
         height, so nothing moves when the data lands. -->
    <div
      :class="cn('relative', fill && 'min-h-42 flex-1')"
      :style="fill ? undefined : { height: `${height}px` }"
      data-slot="chart-skeleton-plot"
    >
      <!-- TimeSeriesChart's padding: 8px above the plot, 24px below for the dates. -->
      <div class="absolute inset-x-0 top-2 bottom-6 flex flex-col justify-between">
        <div v-for="n in RULES" :key="n" class="flex h-0 items-center gap-2.5">
          <Skeleton class="h-2.5 w-5 shrink-0" />
          <div class="h-px flex-1 bg-border/50" />
        </div>
      </div>
      <SkeletonMarks class="absolute top-2 right-2 bottom-6 left-7.5">
        <svg class="block size-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path :d="ghost" class="fill-skeleton/10" data-testid="chart-skeleton-marks" />
        </svg>
      </SkeletonMarks>
      <div class="absolute right-2 bottom-0.5 left-7.5 flex h-4 items-center justify-between">
        <Skeleton v-for="n in DATES" :key="n" class="h-2.5 w-10" />
      </div>
    </div>
    <!-- ChartLegendToggle's row: 12px above entries 20px tall. -->
    <div v-if="legend" class="flex h-8 items-end justify-center gap-4 pb-0.5" data-slot="chart-skeleton-legend">
      <span v-for="n in LEGEND" :key="n" class="flex items-center gap-1.5">
        <Skeleton class="size-2 rounded-xs" />
        <Skeleton class="h-2.5 w-14" />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
// A chart's loading state, through AsyncBoundary's `chart` preset. The tint and
// the pulse are the kit Skeleton's, as everywhere else.
import { computed } from "vue";

import { Skeleton } from "@/components/ui/skeleton";
import SkeletonMarks from "@/components/kit/SkeletonMarks.vue";
import { cn } from "@/lib/utils";

const props = withDefaults(
  defineProps<{
    kind?: "area" | "bar";
    // The chart's height in pixels, which `fill` replaces.
    height?: number;
    // Hold the legend's row under the plot.
    legend?: boolean;
    // Grow into a flex column, as a chart with `fill` does.
    fill?: boolean;
  }>(),
  { kind: "bar", height: 192, legend: false, fill: false },
);

const RULES = 5;
const DATES = 5;
const LEGEND = 3;

// Heights as fractions of the plot, so the ghost reads as a chart rather than
// a grey block. The same run as ActivityHistogram's placeholder bars.
const PATTERN = [0.3, 0.45, 0.35, 0.6, 0.5, 0.7, 0.4, 0.55, 0.8, 0.65, 0.45, 0.5, 0.35, 0.6, 0.75, 0.5, 0.4, 0.3];
const BARS = 24;
const BAR_WIDTH = 0.6;

const y = (fraction: number) => (100 - fraction * 100).toFixed(1);

const ghost = computed(() => {
  if (props.kind === "area") {
    const last = PATTERN.length - 1;
    const line = PATTERN.map((v, i) => `${i ? "L" : "M"}${((i / last) * 100).toFixed(1)} ${y(v * 0.8)}`).join(" ");
    return `${line} L100 100 L0 100 Z`;
  }
  const band = 100 / BARS;
  return Array.from({ length: BARS }, (_, i) => {
    const x = i * band + (band * (1 - BAR_WIDTH)) / 2;
    return `M${x.toFixed(2)} 100 V${y(PATTERN[i % PATTERN.length])} h${(band * BAR_WIDTH).toFixed(2)} V100 Z`;
  }).join(" ");
});
</script>
